import {inject, Injectable} from '@angular/core';
import {NzModalService} from 'ng-zorro-antd/modal';
import {TranslocoService} from '@jsverse/transloco';
import {environment} from '../../environments/environment';
import {SystemApiService} from './apis/system.api.service';
import {isVersionOutdated} from '../utils/compare-version';

/** 版本检查间隔 */
const CHECK_INTERVAL_MS = 30 * 60 * 1000;
const LAST_CHECK_AT_KEY = 'mp-installer-portal:version-check-at';

/** 由 AppComponent 提供，以便复用组件级 NzModalModule */
@Injectable()
export class VersionCheckService {
  private readonly systemApi = inject(SystemApiService);
  private readonly modal = inject(NzModalService);
  private readonly transloco = inject(TranslocoService);

  private checking = false;
  private promptOpen = false;
  private started = false;
  private timerId: ReturnType<typeof setInterval> | null = null;

  private readonly debugMode = !environment.production;

  private readonly onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') {
      return;
    }
    // debug：每次 tab 活跃都检查；生产：仅超过间隔才补检
    if (this.debugMode) {
      void this.checkUpdate();
      return;
    }
    void this.checkIfDue({allowFirstCheck: false});
  };

  /**
   * 启动定时检查（不在启动时立即请求）。
   * - 每 30 分钟检查一次
   * - 页签重新可见时，若距上次检查已超过 30 分钟则补检
   */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.timerId = setInterval(() => {
      // 定时器到期：允许「从未检查过」的首次检查
      void this.checkIfDue({allowFirstCheck: true});
    }, CHECK_INTERVAL_MS);

    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /** 距上次检查已满间隔才真正请求 */
  async checkIfDue(options: {allowFirstCheck: boolean}): Promise<void> {
    if (!this.isCheckDue(options.allowFirstCheck)) {
      return;
    }
    await this.checkUpdate();
  }

  async checkUpdate(): Promise<void> {
    if (this.checking || this.promptOpen) {
      return;
    }
    this.checking = true;
    try {
      const res = await this.systemApi.checkUpdate();
      // 无论是否过时，成功拿到响应即记时，避免反复打接口
      this.markChecked();

      if (res?.code) {
        return;
      }

      const currentVersion = environment.version || '0.0.0';
      const minVersion = res?.result?.min_version || '0.0.0';
      if (!isVersionOutdated(currentVersion, minVersion)) {
        return;
      }

      this.showOutdatedPrompt();
    } catch {
      // 网络失败不记时，便于恢复后尽快补检；仍受 checking 锁保护
    } finally {
      this.checking = false;
    }
  }

  private isCheckDue(allowFirstCheck: boolean): boolean {
    const lastCheckAt = this.getLastCheckAt();
    if (!lastCheckAt) {
      return allowFirstCheck;
    }
    return Date.now() - lastCheckAt >= CHECK_INTERVAL_MS;
  }

  private getLastCheckAt(): number | null {
    const raw = localStorage.getItem(LAST_CHECK_AT_KEY);
    if (!raw) {
      return null;
    }
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  private markChecked(): void {
    localStorage.setItem(LAST_CHECK_AT_KEY, String(Date.now()));
  }

  private showOutdatedPrompt(): void {
    if (this.promptOpen) {
      return;
    }
    this.promptOpen = true;

    const ref = this.modal.warning({
      nzTitle: this.transloco.translate('app.version_check.title'),
      nzContent: this.transloco.translate('app.version_check.content'),
      nzClosable: false,
      nzMaskClosable: false,
      nzKeyboard: false,
      nzCentered: true,
      nzCancelText: null,
      nzOkText: this.transloco.translate('app.version_check.refresh'),
      nzOnOk: () => this.forceReload(),
    });

    ref.afterClose.subscribe(() => {
      this.promptOpen = false;
    });
  }

  /** 清缓存并硬刷新，拉取最新静态资源 */
  private async forceReload(): Promise<void> {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
    } catch {
      // ignore cache clear errors
    }

    const url = new URL(window.location.href);
    url.searchParams.set('_r', String(Date.now()));
    window.location.replace(url.toString());
  }
}
